import { getAccount, selectAccount } from "app/redux/accountSlice";
import { getCart, selectCart } from "app/redux/cartSlice";
import { formatLocalTime, setActiveShopTimeZone } from "app/utils/timezone";
import { useQuery } from "app/utils/useQuery";
import { addMinutes, format } from "date-fns";
import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PaymentMethodTypes } from "app/order/useOrder";
import { calculateBookingTotals } from "app/order/bookingTotals";

export const useOrderComplete = () => {
  const { nameKey } = useParams();
  const dispatch = useDispatch();
  const query = useQuery();
  const paymentMethod = query.get("payment");
  const account = useSelector(selectAccount);
  const cart = useSelector(selectCart);
  const [appointmentStartTimes, setAppointmentStartTimes] = React.useState<any>(
    []
  );

  const groupedMerchantServices =
    Object.values(cart).length > 0
      ? Object.values(
          cart?.merchantServices.reduce((acc, item) => {
            const name = item.employee.name;
            if (!acc[name]) {
              acc[name] = { name, services: [], avatar: item.employee.avatar };
            }
            acc[name].services.push(item);
            return acc;
          }, {})
        )
      : [];

  const init = async () => {
    const cartUid = query.get("cart");
    if (!cartUid) return;

    try {
      await dispatch(getAccount({ nameKey } as any) as any).unwrap();
    } catch (error) {
      console.error(error);
    }

    try {
      const fetchedCart = await dispatch(
        getCart({ uid: cartUid }) as any
      ).unwrap();

      setActiveShopTimeZone(fetchedCart?.shop?.timezone?.timeZoneId);

      let runningTime = new Date(fetchedCart!.startTime);
      const times = [];
      for (const service of fetchedCart.merchantServices) {
        const duration = service.duration;

        // formatLocalTime takes a UTC string and converts to local display
        times.push(formatLocalTime(runningTime.toISOString()));

        // Advance the running time in UTC
        runningTime = addMinutes(runningTime, duration);
      }
      setAppointmentStartTimes(times);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const subtotalCents = Number(cart?.subtotalCents) || 0;
  // Same calculation checkout charged with; card modes carry the card fee.
  const paidByCard = [
    PaymentMethodTypes.CREDIT_CARD,
    PaymentMethodTypes.DEPOSIT,
    PaymentMethodTypes.PRE_AUTH,
  ].includes(paymentMethod as PaymentMethodTypes);
  const fullTotalCents = calculateBookingTotals({
    subtotalCents,
    taxPercentage: cart?.shop?.taxPercentage,
    account,
    payByCash: !paidByCard,
  }).totalCents;
  const depositType = account?.depositType;
  const depositValue = Number(account?.depositValue) || 0;
  const paidCents = paymentMethod === PaymentMethodTypes.DEPOSIT
    ? (depositType === "fixed"
        ? Math.round(depositValue * 100)
        : Math.round(fullTotalCents * (depositValue / 100)))
    : paymentMethod === PaymentMethodTypes.CREDIT_CARD
      ? fullTotalCents
      : 0;
  const pendingCents = Math.max(0, fullTotalCents - paidCents);

  return {
    account,
    cart,
    appointmentStartTimes,
    paymentMethod,
    groupedMerchantServices,
    fullTotalCents,
    paidCents,
    pendingCents,
  };
};
